import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import services from "../../services";
import moment from "moment";
import { toast } from "react-toastify";
import { LuSendHorizontal } from "react-icons/lu";
import { MdAttachFile, MdRefresh, MdKeyboardArrowDown } from "react-icons/md";
import { Typography, Menu, MenuItem } from "@mui/material";
import { requestForToken, onMessageListener } from "../../firebase";
import AppModal from "../../common/AppModal";
import { FiAlertCircle } from "react-icons/fi";

const GREEN        = '#1a7d3e';
const GREEN_DARK   = '#145f30';
const GREEN_LIGHT  = '#f0faf4';
const GREEN_BORDER = '#b7e4c7';

const ConversationTab = ({ ticketId, parentOrigin, handleRefreshTask }) => {
    const navigate = useNavigate();
    const [ticketData, setTicketData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [replyMessage, setReplyMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    const isInitialLoad = useRef(true);
    const scrollContainerRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    const [anchorEl, setAnchorEl] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [updatingStatus, setUpdatingStatus] = useState(false);

    const getStatusDetails = (status) => {
        switch (status) {
            case 'OPEN':
                return { label: 'Open', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', dot: GREEN };
            case 'IN_PROGRESS':
                return { label: 'In Progress', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', dot: '#ea580c' };
            case 'CLOSED_COMPLETE':
                return { label: 'Closed Complete', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', dot: GREEN_DARK };
            case 'CLOSED_INCOMPLETE':
                return { label: 'Closed Incomplete', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: '#dc2626' };
            default:
                return { label: status || 'Unknown', color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', dot: '#6b7280' };
        }
    };

    const ancestor = window?.location?.ancestorOrigins?.[0];

    const { currentUserId, currentUserRole } = (() => {
        try {
            let userData = JSON.parse(localStorage.getItem("userData") || "{}");
            if (typeof userData === "string") userData = JSON.parse(userData);
            return { currentUserId: userData?.id, currentUserRole: userData?.role };
        } catch {
            return { currentUserId: undefined, currentUserRole: undefined };
        }
    })();

    const handleStatusClick  = (e) => setAnchorEl(e.currentTarget);
    const handleStatusClose  = ()  => setAnchorEl(null);

    const handleStatusSelect = (status) => {
        handleStatusClose();
        if (status !== ticketData.ticketStatus) {
            setSelectedStatus(status);
            setStatusModalOpen(true);
        }
    };

    const confirmStatusUpdate = async () => {
        setUpdatingStatus(true);
        try {
            await services.support.UPDATE_TICKET(ticketId, { ticketStatus: selectedStatus });
            toast.success("Ticket status updated successfully!");
            setStatusModalOpen(false);
            fetchTicket();
        } catch (err) {
            console.error("Error updating status:", err);
            toast.error("Failed to update status");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const scrollToBottom = (behavior = "smooth") => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior });
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            setIsAtBottom(scrollHeight - scrollTop - clientHeight < 50);
        }
    };

    const fetchTicket = async (isInitial = false) => {
        if (!ticketId) { setLoading(false); return; }
        try {
            if (isInitial) setLoading(true);
            const response = await services.support.getTicketById(ticketId);
            if (response.success) {
                setTicketData(response.data);
                if (handleRefreshTask) handleRefreshTask();
            } else {
                setError("Failed to fetch ticket details");
            }
        } catch (err) {
            console.error("Error fetching ticket:", err);
            setError(err.message || "An error occurred");
        } finally {
            if (isInitial) setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchTicket();
        setRefreshing(false);
    };

    useEffect(() => {
        isInitialLoad.current = true;
        fetchTicket(true);
    }, [ticketId]);

    useEffect(() => {
        if (ticketData) {
            if (isInitialLoad.current) {
                setTimeout(() => { scrollToBottom("auto"); isInitialLoad.current = false; setIsAtBottom(true); }, 100);
            } else if (isAtBottom) {
                scrollToBottom("smooth");
            }
        }
    }, [ticketData, isAtBottom]);

    useEffect(() => {
        if (typeof Notification !== "undefined" && Notification.permission !== "denied") {
            requestForToken().then((token) => {
                if (token) {
                    const stored = localStorage.getItem('fbToken');
                    if (stored !== token) localStorage.setItem('fbToken', token);
                }
            });
        }
        const unsubscribe = onMessageListener((payload) => {
            console.log("Received foreground message:", payload);
            toast.info(`New message: ${payload?.notification?.title || "from support"}`);
            if (payload?.data?.ticketId === ticketId) fetchTicket();
        });
    }, [ticketId]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) { setSelectedFile(file); toast.info(`Selected file: ${file.name}`); }
    };

    const handleSendReply = async () => {
        if (!replyMessage.trim() && !selectedFile) {
            toast.error("Please enter a message or select a file");
            return;
        }
        try {
            setSending(true);
            let fileUrl = "";
            if (selectedFile) {
                const formData = new FormData();
                formData.append("image", selectedFile);
                const uploadRes = await services.support.uploadSingleFile(formData);
                fileUrl = uploadRes?.data?.image || "";
            }
            let userData = JSON.parse(localStorage.getItem("userData") || "{}");
            if (typeof userData === "string") {
                try {
                    userData = JSON.parse(userData);
                } catch {
                    userData = {};
                }
            }
            const senderRole = userData?.role;
            const senderId = userData?.id;
            const senderName = userData?.name;
            if (!senderRole) {
                console.warn("[sendReply] userData.role missing in localStorage");
            }
            const payload = {
                description: [{
                    message: replyMessage || "ok",
                    fileUrl,
                    senderRole: senderRole || "User",
                    senderId,
                    senderName,
                    time: new Date().toISOString(),
                }],
                documents: []
            };
            const result = await services.support.replyToTicket(ticketId, payload);
            if (result.success) {
                toast.success("Reply sent successfully!");
                setReplyMessage("");
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                fetchTicket();
            } else {
                toast.error(result.message || "Failed to send reply");
            }
        } catch (err) {
            console.error("Error sending reply:", err);
            toast.error("An error occurred while sending the reply");
        } finally {
            setSending(false);
        }
    };

    const flattenMessages = (items) => {
        let result = [];
        (Array.isArray(items) ? items : [items]).forEach(item => {
            if (!item) return;
            if (Array.isArray(item.message)) {
                flattenMessages(item.message).forEach(nested => {
                    result.push({ ...nested, fileUrl: nested.fileUrl || item.fileUrl });
                });
            } else if (item.message !== undefined && item.message !== null) {
                result.push({
                    time: item.time,
                    fileUrl: item.fileUrl,
                    senderRole: item.senderRole,
                    senderId: item.senderId,
                    senderName: item.senderName,
                    message: typeof item.message === 'string' ? item.message : String(item.message),
                });
            }
        });
        return result;
    };

    const getGroupedMessages = () => {
        if (!ticketData?.description) return [];
        let allMessages = flattenMessages(ticketData.description);
        allMessages.sort((a, b) => new Date(a.time) - new Date(b.time));
        allMessages = allMessages.slice(1);
        const groups = {};
        allMessages.forEach(msg => {
            const dateStr = moment(msg.time).format("YYYY-MM-DD");
            if (!groups[dateStr]) groups[dateStr] = [];
            groups[dateStr].push(msg);
        });
        return Object.keys(groups).sort().map(date => ({ date, messages: groups[date] }));
    };

    const getAttachments = (fileUrl) => {
        if (!fileUrl) return [];
        if (Array.isArray(fileUrl)) {
            return fileUrl
                .map(f => (typeof f === 'string' ? { url: f, name: f } : { url: f?.fileUrl, name: f?.fileName || f?.fileUrl }))
                .filter(f => f.url);
        }
        if (typeof fileUrl === 'string') return [{ url: fileUrl, name: fileUrl }];
        return [];
    };

    const formatDateLabel = (dateStr) => {
        const date      = moment(dateStr);
        const today     = moment().startOf('day');
        const yesterday = moment().subtract(1, 'days').startOf('day');
        if (date.isSame(today, 'd'))     return "TODAY";
        if (date.isSame(yesterday, 'd')) return "YESTERDAY";
        return date.format("D MMMM YYYY").toUpperCase();
    };

    const handleTicketClick = () => {
        const id = ticketData?._id || ticketId;
        if (id) window.parent.location.href = `${parentOrigin}/support-tickets/${id}`;
    };

    if (loading) return <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>Loading conversation…</div>;
    if (error)   return <div style={{ padding: '24px', textAlign: 'center', color: '#ef4444', fontSize: '14px' }}>{error}</div>;
    if (!ticketData) return <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>No conversation data found.</div>;

    const groupedMessages = getGroupedMessages();
    const statusDetail    = getStatusDetails(ticketData.ticketStatus);

    const referenceHref = (() => {
        if (!ticketData) return '#';
        const bType = String(ticketData.bookingType || '').toLowerCase();
        const bInfo = ticketData.booking || {};
        const avail = bInfo.availability || ticketData.availability || {};
        let companyTab = 'accommodation', productId = '', travelDate = '', tripId = bInfo.tripId || 'null', typeStr = bInfo.type || 'manual', tripTitle = '';
        if (bType === 'product')        { companyTab = 'product';        productId = bInfo.productId || '';                             travelDate = bInfo.productDate || bInfo.accBookingDate || ''; }
        else if (bType === 'flight')    { companyTab = 'flight';         productId = avail.flightId || bInfo.flightId;                  travelDate = bInfo.accBookingDate || avail.startDate || ''; }
        else if (bType === 'transfer')  { companyTab = 'transfer';       productId = avail.transferId || bInfo.transferId;             travelDate = bInfo.accBookingDate || avail.dateAndTime?.startDateAndTime || ''; if (bInfo.tripTitle) tripTitle = encodeURIComponent(bInfo.tripTitle); }
        else if (bType === 'ferry')     { companyTab = 'ferry';          productId = bInfo.ferryId || avail.ferryId;                   travelDate = bInfo.accBookingDate || avail.startDate || ''; }
        else if (bType === 'rental')    { companyTab = 'rental';         productId = bInfo.rentalId || avail.carRentalId || '';        travelDate = bInfo.accBookingDate || avail.dropDate || ''; }
        else                            { companyTab = 'accommodation';  productId = avail.accommodationId || bInfo.accomodationId;    travelDate = bInfo.accBookingDate || avail.dropDate || ''; }
        return `${parentOrigin}/salesFeed/${ticketData.bookingId}?companyTab=${companyTab}&productId=${productId}&travelDate=${travelDate}&bookingTripId=${tripId}&bookingTripType=${typeStr}&bookingTripTitle=${tripTitle}`;
    })();

    const refLabel = `${ticketData.type === 'manual' ? 'BCM' : 'BCT'}-${(ticketData.bookingId || ticketData.id || '').slice(0, 8).toUpperCase()}`;

    return (
        <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', padding: '20px 24px', marginTop: '12px', height: 'calc(100vh - 5rem)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* ── Ticket header ── */}
            <div style={{ borderBottom: `1px solid ${GREEN_BORDER}`, paddingBottom: '14px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

                    {/* Left: ticket number + reference + refresh */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span
                            onClick={handleTicketClick}
                            title="Go to ticket details"
                            style={{ fontSize: '17px', fontWeight: '700', color: GREEN, cursor: 'pointer', textDecoration: 'none' }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >
                            Ticket: {ticketData.ticketNumber}
                        </span>

                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Reference:</span>
                        <a
                            href={referenceHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '13px', fontWeight: '700', color: GREEN, textDecoration: 'none', background: GREEN_LIGHT, border: `1px solid ${GREEN_BORDER}`, borderRadius: '6px', padding: '2px 8px' }}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >
                            {refLabel}
                        </a>

                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            title="Refresh"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%', border: `1px solid ${GREEN_BORDER}`, background: GREEN_LIGHT, color: GREEN, cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.5 : 1 }}
                        >
                            <MdRefresh size={18} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />
                        </button>
                    </div>

                    {/* Right: status badge */}
                    <div>
                        <button
                            onClick={handleStatusClick}
                            disabled={updatingStatus}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', border: '1.5px solid', cursor: 'pointer', transition: 'opacity 0.15s' }}
                            className={`${statusDetail.bg} ${statusDetail.color} ${statusDetail.border}`}
                        >
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: statusDetail.dot, flexShrink: 0 }} />
                            {statusDetail.label}
                            <MdKeyboardArrowDown size={16} style={{ opacity: 0.7 }} />
                        </button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleStatusClose}
                            PaperProps={{ elevation: 3, sx: { mt: 1, minWidth: 170, borderRadius: '8px', border: `1px solid ${GREEN_BORDER}`, boxShadow: '0 4px 12px rgba(26,125,62,0.12)' } }}
                        >
                            {[
                                { key: 'OPEN',             label: 'Open',             dot: GREEN },
                                { key: 'IN_PROGRESS',      label: 'In Progress',      dot: '#ea580c' },
                                { key: 'CLOSED_COMPLETE',  label: 'Closed Complete',  dot: GREEN_DARK },
                                { key: 'CLOSED_INCOMPLETE',label: 'Closed Incomplete',dot: '#dc2626' },
                            ].map(s => (
                                <MenuItem key={s.key} onClick={() => handleStatusSelect(s.key)} sx={{ py: 1.2 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: s.dot, flexShrink: 0 }} />
                                        {s.label}
                                    </div>
                                </MenuItem>
                            ))}
                        </Menu>
                    </div>
                </div>

                {/* Subject */}
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                    Subject: <span style={{ fontWeight: '500' }}>{ticketData.subject}</span>
                </div>

                {/* Description */}
                {groupedMessages?.[0]?.messages?.[0]?.message && (
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>Description:</div>
                        <div style={{ fontSize: '13px', color: '#4b5563', background: GREEN_LIGHT, border: `1px solid ${GREEN_BORDER}`, borderRadius: '8px', padding: '10px 12px', maxHeight: '80px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-words', lineHeight: '1.5' }}>
                            {groupedMessages[0].messages[0].message}
                        </div>
                    </div>
                )}

                {/* Meta row */}
                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280', flexWrap: 'wrap' }}>
                    <span>Priority: <span style={{ fontWeight: '600', color: '#ea580c' }}>{ticketData.priority}</span></span>
                    <span>Created: <span style={{ fontWeight: '500', color: '#374151' }}>{ticketData.createdAt ? moment(ticketData.createdAt).format("DD MMM YYYY HH:mm") : 'N/A'}</span></span>
                    <span>Created By: <span style={{ fontWeight: '600', color: GREEN }}>{ticketData.User?.name}</span></span>
                </div>
            </div>

            {/* ── Messages ── */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', scrollBehavior: 'smooth' }}
            >
                {groupedMessages.length > 0 ? groupedMessages.map((group, gIdx) => (
                    <div key={gIdx} style={{ marginBottom: '20px' }}>
                        {/* Date separator */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px', position: 'sticky', top: 0, zIndex: 10, padding: '6px 0' }}>
                            <span style={{ background: GREEN_LIGHT, color: GREEN_DARK, fontSize: '10px', fontWeight: '700', padding: '3px 12px', borderRadius: '20px', border: `1px solid ${GREEN_BORDER}`, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                {formatDateLabel(group.date)}
                            </span>
                        </div>

                        {group.messages.map((msg, mIdx) => {
                            const isOwnMessage = msg.senderId
                                ? String(msg.senderId) === String(currentUserId)
                                : String(msg.senderRole).toLowerCase() === String(currentUserRole).toLowerCase();
                            const displayName = isOwnMessage
                                ? (msg.senderName || 'You')
                                : (msg.senderName || ticketData.User?.name || msg.senderRole);
                            return (
                                <div key={mIdx} style={{ marginBottom: '12px', display: 'flex', justifyContent: isOwnMessage ? 'flex-end' : 'flex-start' }}>
                                    <div style={{
                                        maxWidth: '72%', minWidth: '100px', width: 'fit-content',
                                        borderRadius: isOwnMessage ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                        padding: '10px 14px',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        background: isOwnMessage ? `linear-gradient(135deg, ${GREEN} 0%, #34a85a 100%)` : '#f3f4f6',
                                        border: isOwnMessage ? 'none' : '1px solid #e5e7eb',
                                        color: isOwnMessage ? '#fff' : '#1e293b',
                                        overflowWrap: 'anywhere',
                                        boxSizing: 'border-box',
                                    }}>
                                        {/* Sender + time */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '6px', paddingBottom: '6px', borderBottom: isOwnMessage ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e5e7eb' }}>
                                            <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: isOwnMessage ? 0.85 : 0.6 }}>
                                                {displayName}
                                            </span>
                                            <span style={{ fontSize: '10px', opacity: isOwnMessage ? 0.7 : 0.5, whiteSpace: 'nowrap' }}>
                                                {moment(msg.time).format("hh:mm A")}
                                            </span>
                                        </div>

                                        {/* Message text */}
                                        <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', fontWeight: '450' }}>
                                            {msg.message}
                                        </p>

                                        {/* Attachment */}
                                        {getAttachments(msg.fileUrl).map((att, aIdx) => {
                                            const fullUrl = att.url?.startsWith('http') ? att.url : `${process.env.REACT_APP_IMAGE_URL}/${att.url}`;
                                            return (
                                                <div key={aIdx} style={{ marginTop: '10px', paddingTop: '8px', borderTop: isOwnMessage ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {/\.(jpg|jpeg|png|gif|webp)$/i.test(att.url) && (
                                                        <img
                                                            src={fullUrl}
                                                            alt="Attachment"
                                                            style={{ maxWidth: '200px', borderRadius: '8px', cursor: 'pointer' }}
                                                            onClick={() => window.open(fullUrl, '_blank')}
                                                        />
                                                    )}
                                                    <a
                                                        href={fullUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: '700', textDecoration: 'none', color: isOwnMessage ? 'rgba(255,255,255,0.85)' : GREEN, background: isOwnMessage ? 'rgba(255,255,255,0.15)' : GREEN_LIGHT, padding: '3px 8px', borderRadius: '5px', width: 'fit-content' }}
                                                    >
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                                                        VIEW ATTACHMENT
                                                    </a>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )) : (
                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: '14px', fontStyle: 'italic' }}>
                        No messages in this conversation.
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* ── Reply box ── */}
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${GREEN_BORDER}` }}>
                <div
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: GREEN_LIGHT, borderRadius: '10px', padding: '6px 10px', border: `1.5px solid ${GREEN_BORDER}`, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                    onFocus={() => {}}
                >
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
                    <button
                        onClick={() => fileInputRef.current.click()}
                        disabled={sending}
                        title="Attach file"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', background: 'none', border: 'none', cursor: 'pointer', color: selectedFile ? GREEN : '#9ca3af', transition: 'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color = GREEN}
                        onMouseLeave={e => e.currentTarget.style.color = selectedFile ? GREEN : '#9ca3af'}
                    >
                        <MdAttachFile size={20} />
                    </button>

                    <input
                        type="text"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                        placeholder="Type your reply…"
                        disabled={sending}
                        style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: '13px', color: '#1e293b', padding: '4px 0' }}
                    />

                    <button
                        onClick={handleSendReply}
                        disabled={sending || (!replyMessage.trim() && !selectedFile)}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '34px', height: '34px', borderRadius: '50%', border: 'none',
                            background: (sending || (!replyMessage.trim() && !selectedFile)) ? '#e5e7eb' : `linear-gradient(135deg, ${GREEN} 0%, #34a85a 100%)`,
                            color: (sending || (!replyMessage.trim() && !selectedFile)) ? '#9ca3af' : '#fff',
                            cursor: (sending || (!replyMessage.trim() && !selectedFile)) ? 'not-allowed' : 'pointer',
                            transition: 'background 0.15s',
                            boxShadow: (sending || (!replyMessage.trim() && !selectedFile)) ? 'none' : '0 2px 6px rgba(26,125,62,0.3)',
                        }}
                    >
                        {sending
                            ? <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            : <LuSendHorizontal size={18} />
                        }
                    </button>
                </div>

                {selectedFile && (
                    <div style={{ marginTop: '6px', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: GREEN_DARK, background: GREEN_LIGHT, border: `1px solid ${GREEN_BORDER}`, padding: '3px 10px', borderRadius: '6px' }}>
                        <span style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFile.name}</span>
                        <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: '700', cursor: 'pointer', padding: 0, fontSize: '12px' }}>✕</button>
                    </div>
                )}
            </div>

            {/* ── Status confirm modal (AppModal) ── */}
            <AppModal
                show={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                icon={<FiAlertCircle size={18} color="#fff" />}
                title="Update Ticket Status"
                subtitle="Confirm the status change below"
                maxWidth="420px"
                footer={
                    <>
                        <button
                            onClick={() => setStatusModalOpen(false)}
                            disabled={updatingStatus}
                            style={{ padding: '8px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmStatusUpdate}
                            disabled={updatingStatus}
                            style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', background: `linear-gradient(135deg, ${GREEN} 0%, #34a85a 100%)`, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: updatingStatus ? 'not-allowed' : 'pointer', opacity: updatingStatus ? 0.7 : 1, boxShadow: '0 2px 8px rgba(26,125,62,0.25)' }}
                        >
                            {updatingStatus ? 'Updating…' : 'Confirm'}
                        </button>
                    </>
                }
            >
                <p style={{ margin: 0, fontSize: '14px', color: '#4b5563', lineHeight: '1.6' }}>
                    Change ticket status to{' '}
                    <strong style={{ color: '#1e293b' }}>{selectedStatus.replace(/_/g, ' ')}</strong>?
                </p>
            </AppModal>
        </div>
    );
};

export default ConversationTab;
